import type {
  BuildingGeomAnalytic,
  BuildingGeomAttributeTag,
  BuildingGeomAttributeValueType,
  BuildingGeomCondition,
  BuildingGeomOperation,
  BuildingGeomParent,
  BuildingGeomPosition,
  BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Face } from "geom/brep";
import { tagFace, tagFaces, type FaceTagBase } from "geom/brep";

export function tagBuildingFaces(faces: Face[], tags: BuildingGeomTag | BuildingGeomTag[]): Face[] {
  return tagFaces(faces, tags);
}

export function tagBuildingFace(face: Face, tags: BuildingGeomTag | BuildingGeomTag[]): Face {
  return tagFace(face, tags);
}

export function isBuildingGeomTag(tag: FaceTagBase): tag is BuildingGeomTag {
  if (!tag["type"]) {
    return false;
  }
  switch (tag["type"]) {
    case "CONDITION":
    case "POSITION":
    case "PARENT":
    case "OPERATION":
    case "ANALYTIC":
    case "MATERIAL":
    case "ATTRIBUTE":
      return true;
    default:
      return false;
  }
}

export function isBuildingGeomConditionTag(tag: BuildingGeomTag): tag is BuildingGeomCondition {
  return tag["type"] === "CONDITION";
}

export function isBuildingGeomAnalyticTag(tag: BuildingGeomTag): tag is BuildingGeomAnalytic {
  return tag["type"] === "ANALYTIC_CONDITION";
}

export function isBuildingGeomAttributeTag(tag: BuildingGeomTag): tag is BuildingGeomAttributeTag {
  return tag["type"] === "ATTRIBUTE";
}

export function isBuildingGeomPositionTag(tag: BuildingGeomTag): tag is BuildingGeomPosition {
  return tag["type"] === "POSITION";
}

export function isBuildingGeomOperationTag(tag: BuildingGeomTag): tag is BuildingGeomOperation {
  return tag["type"] === "OPERATION";
}

export function isBuildingGeomParentTag(tag: FaceTagBase): tag is BuildingGeomParent {
  return isBuildingGeomTag(tag) && tag.type === "PARENT";
}

export function isBuildingGeomAttributeTagOfValueType<T extends BuildingGeomAttributeValueType>(valueType: T) {
  return (tag: BuildingGeomTag): tag is Extract<BuildingGeomAttributeTag<T>, { valueType: T }> =>
    isBuildingGeomAttributeTag(tag) && tag.valueType === valueType;
}

export function isBuildingGeomAttributeTagOfNameAndValueType<T extends BuildingGeomAttributeValueType>(
  name: string,
  valueType: T,
) {
  return (tag: BuildingGeomTag): tag is Extract<BuildingGeomAttributeTag<T>, { valueType: T }> =>
    isBuildingGeomAttributeTag(tag) && tag.attributeName === name && tag.valueType === valueType;
}

export function getSubMassParentTag(face: Face): BuildingGeomParent | undefined {
  if (!face.tags) {
    return undefined;
  }
  for (const tag of face.tags) {
    if (isBuildingGeomParentTag(tag) && tag.parentType === "WALL_SUB_MASS") {
      return tag;
    }
  }
  return undefined;
}

export function handleFromBuildingGeomParentTag(
  tag: BuildingGeomParent,
): DocEntityHandle | BuildingEntityHandle | undefined {
  switch (tag.parentType) {
    case "WALL":
      return {
        entityType: "WALL",
        id: tag.parentId,
      };
    case "ROOF":
    case "LEVEL":
    case "ROOF_SYSTEM":
      return {
        type: tag.parentType,
        id: tag.parentId,
      };
    case "WALL_MASS":
    // TODO: generated types that are not yet BuildingEntity types
    default:
      return undefined;
  }
}
